import { Add as AddIcon } from '@mui/icons-material';
import type { ChipProps as MuiChipProps } from '@mui/material/Chip';
import MuiChip from '@mui/material/Chip';
import type { Palette } from '@mui/material/styles';
import { darken, useTheme } from '@mui/material/styles';
import is from '@sindresorhus/is';
import type { FunctionComponent, MouseEventHandler } from 'react';

export const skillChipHeight = '36px';

type PaletteKey<T extends keyof Palette> = T;

type ChipColor = PaletteKey<'hardskill' | 'language' | 'skillrecommendation' | 'softskill'>;

export type ChipPropType = 'hard' | 'language' | 'skillrecommendation' | 'soft' | undefined;

type ChipProps = Pick<MuiChipProps, 'size' | 'variant'> & {
    readonly isDisabled?: boolean | undefined;
    readonly isSynced?: boolean | undefined;
    readonly label: string;
    readonly onClick?: MouseEventHandler | undefined;
    readonly showAddIcon?: boolean;
    readonly skillId: string;
    readonly type?: ChipPropType;
};

type ChipPropsWithOnClick = ChipProps & { readonly onClick: MouseEventHandler };

export const skillTypeColorMap: Record<NonNullable<ChipPropType>, ChipColor> = {
    hard: 'hardskill',
    language: 'language',
    skillrecommendation: 'skillrecommendation',
    soft: 'softskill',
};

const darkenCoefficient = 0.1;

const isChipWithOnClick = (props: ChipProps): props is ChipPropsWithOnClick => is.function(props.onClick) === true;

export const SkillChip: FunctionComponent<ChipProps> = (props) => {
    const theme = useTheme();
    const { isDisabled, isSynced, label, onClick, showAddIcon, type, variant } = props;
    const currentTypeColor: ChipColor = skillTypeColorMap[type ?? 'skillrecommendation'];
    const clickable = isChipWithOnClick(props);
    const icon = showAddIcon === true ? { icon: <AddIcon /> } : {};
    const hoverColor = clickable ? darken(theme.palette[currentTypeColor].main, darkenCoefficient) : undefined;

    return (
        <MuiChip
            {...icon}
            clickable={clickable}
            color={currentTypeColor}
            disabled={isDisabled ?? false}
            label={label}
            sx={{
                '&:hover': { backgroundColor: hoverColor },
                height: skillChipHeight,
                opacity: isSynced === false ? 0.5 : 1,
                typography: 'h6',
            }}
            title={label}
            variant={variant ?? 'filled'}
            onClick={onClick}
        />
    );
};
